/** Base error class for all Spectrum SDK errors */
export class SpectrumError extends Error {
  public readonly status?: number;
  public readonly path?: string;

  constructor(message: string, status?: number, path?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'SpectrumError';
    this.status = status;
    this.path = path;
  }
}

/** API returned an error response */
export class ApiError extends SpectrumError {
  public readonly body?: unknown;

  constructor(message: string, status: number, path: string, body?: unknown) {
    super(message, status, path);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'ApiError';
    this.body = body;
  }
}

/** Request was rate limited (HTTP 429) */
export class RateLimitError extends SpectrumError {
  public readonly retryAfter?: number;

  constructor(path: string, retryAfter?: number) {
    super(`Rate limited on ${path}. Retry after ${retryAfter ?? 'unknown'}ms`, 429, path);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

/** Chain slug not recognized */
export class ChainNotFoundError extends SpectrumError {
  public readonly chain: string;

  constructor(chain: string) {
    super(`Unknown chain: ${chain}`);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'ChainNotFoundError';
    this.chain = chain;
  }
}

/** Request timed out */
export class TimeoutError extends SpectrumError {
  public readonly timeout: number;

  constructor(path: string, timeout: number) {
    super(`Request to ${path} timed out after ${timeout}ms`, undefined, path);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'TimeoutError';
    this.timeout = timeout;
  }
}

/** Network/connection error */
export class NetworkError extends SpectrumError {
  constructor(path: string, cause?: Error) {
    super(`Network error on ${path}: ${cause?.message ?? 'unknown'}`, undefined, path);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'NetworkError';
    // Use standard Error.cause (ES2022) instead of shadowing with a narrower type
    if (cause) {
      this.cause = cause;
    }
  }
}

/** Invalid parameter passed to the SDK */
export class ValidationError extends SpectrumError {
  public readonly field: string;

  constructor(field: string, message: string) {
    super(`Invalid parameter '${field}': ${message}`);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'ValidationError';
    this.field = field;
  }
}
